/**
 * BrandKitsPage — Sprint 3 T3.2 minimum: surface a single default kit
 * with editing for colors / typography / logo / footer + sender. The
 * server auto-creates a blank default if the tenant doesn't have one
 * yet, so this page never has to render an empty state on first load.
 *
 * Multi-kit list view + create/delete UI is Phase 2 polish — backlog.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PortalShell } from "../components/PortalShell";
import { BrandKitForm } from "../components/brand-kit/BrandKitForm";
import { BrandVoiceWizard } from "../components/brand-kit/voice/BrandVoiceWizard";
import { Button } from "../components/ui/button";
import { ApiError } from "../lib/api";
import { useAuthStore } from "../stores/auth";
import {
  getDefaultBrandKit,
  patchBrandKit,
  type BrandKit,
  type BrandKitPatch,
} from "../lib/brandKits";

export function BrandKitsPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [voiceWizardOpen, setVoiceWizardOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery<BrandKit, ApiError>({
    queryKey: ["brand-kit", "default"],
    queryFn: getDefaultBrandKit,
  });

  const hasVoice = data?.data.voice !== undefined && data?.data.voice !== null;
  // Voice writes are admin-only server-side (AI-09 §G1) — a member
  // would only discover the 403 at Save/Analyze, so don't offer the
  // entry point at all. The badge stays: viewing is for everyone.
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");

  const mutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: BrandKitPatch }) =>
      patchBrandKit(id, patch),
    onSuccess: (updated) => {
      queryClient.setQueryData(["brand-kit", "default"], updated);
      setSaveError(null);
    },
    onError: (err) => {
      setSaveError(
        err instanceof ApiError
          ? err.problem.detail || err.problem.title
          : t("brand_kit.errors.save_failed"),
      );
    },
  });

  return (
    <PortalShell>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-neutral-900">
            {t("brand_kit.title")}
            {hasVoice && (
              <span className="rounded-full bg-success-50 px-2 py-0.5 text-xs font-medium text-success-700">
                {t("brandVoice.badge")}
              </span>
            )}
          </h1>
          <p className="text-sm text-neutral-600">{t("brand_kit.subtitle")}</p>
        </div>
        {data !== undefined && isAdmin && (
          <Button type="button" variant="outline" size="sm" onClick={() => setVoiceWizardOpen(true)}>
            {hasVoice ? t("brandVoice.edit_button") : t("brandVoice.configure_button")}
          </Button>
        )}
      </header>

      {voiceWizardOpen && data !== undefined && (
        <BrandVoiceWizard
          kit={data}
          onClose={() => setVoiceWizardOpen(false)}
          onSaved={(updated) => {
            queryClient.setQueryData(["brand-kit", "default"], updated);
          }}
        />
      )}

      {isLoading && (
        <p className="text-sm text-neutral-500">{t("common.loading")}</p>
      )}

      {error !== null && (
        <div
          role="alert"
          className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
        >
          {error.problem.detail ||
            error.problem.title ||
            t("brand_kit.errors.load_failed")}
          <button
            type="button"
            onClick={() => void refetch()}
            className="ml-2 font-medium underline"
          >
            {t("common.retry")}
          </button>
        </div>
      )}

      {data !== undefined && (
        <BrandKitForm
          kit={data}
          isSaving={mutation.isPending}
          saveError={saveError}
          onSave={async (patch) => {
            await mutation.mutateAsync({ id: data.id, patch });
          }}
        />
      )}
    </PortalShell>
  );
}
